import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, X, Loader2, Sparkles, MapPin, Pencil, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Pulse — the city's dating/social app. Character names only, swipe deck,
   mutual likes match with a suggested first-meet spot delivered by city mail. */

const DEMO_DECK = [
  { pc: 22110, name: "Ada King", tagline: "night markets & loud engines", interests: ["cars", "ramen"], looking: "everyone" },
  { pc: 88014, name: "Mia Torres", tagline: "permits pending, vibes immaculate", interests: ["events"], looking: "friends" },
];
const DEMO_MATCHES = [{ id: "pm1", with_name: "Ray Dalton", with_pc: 22111, spot: "the Night Market", at: "" }];

export default function PulseApp({ demo, serverId, token, accent, sfx }) {
  const [tab, setTab] = useState("deck"); // deck | matches | profile
  const [deck, setDeck] = useState(demo ? DEMO_DECK : []);
  const [mine, setMine] = useState(null);
  const [matches, setMatches] = useState(demo ? DEMO_MATCHES : []);
  const [form, setForm] = useState({ tagline: "", interests: "", looking: "everyone", active: true });
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/pulse/deck`, { params: { token } }).then((r) => {
      setDeck(r.data.deck); setMine(r.data.my_profile);
      if (r.data.my_profile) setForm({ tagline: r.data.my_profile.tagline, interests: (r.data.my_profile.interests || []).join(", "), looking: r.data.my_profile.looking, active: r.data.my_profile.active });
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/nc/servers/${serverId}/pulse/matches`, { params: { token } }).then((r) => setMatches(r.data.matches)).catch(() => {});
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  const swipe = async (target, dir) => {
    if (busy) return;
    setBusy(target); sfx?.keyTick();
    const finish = (msg, matched) => {
      setDeck((d) => d.filter((c) => c.pc !== target));
      matched ? toast.success(msg) : toast(msg);
      if (matched && !demo) api.get(`/nc/servers/${serverId}/pulse/matches`, { params: { token } }).then((r) => setMatches(r.data.matches)).catch(() => {});
    };
    try {
      if (demo) { finish(dir === "like" && target === 22110 ? "It's a match — check Mail (demo)" : (dir === "like" ? "Liked (demo)" : "Passed (demo)"), dir === "like" && target === 22110); return; }
      const r = await api.post(`/nc/servers/${serverId}/pulse/swipe`, { target_pc: target, dir }, { params: { token } });
      finish(r.data.message, r.data.matched);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const saveProfile = async () => {
    if (busy) return;
    setBusy("save"); sfx?.keyTick();
    try {
      if (demo) { toast.success("Preview: profile saved (demo)"); return; }
      const r = await api.post(`/nc/servers/${serverId}/pulse/profile`,
        { tagline: form.tagline, interests: form.interests.split(",").map((x) => x.trim()).filter(Boolean), looking: form.looking, active: form.active },
        { params: { token } });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const card = deck[0];
  return (
    <div data-testid="pulse-app" className="flex-1 flex flex-col gap-3 overflow-y-auto pb-4" style={{ scrollbarWidth: "none" }}>
      <div className="flex items-center gap-2">
        <p className="font-sans font-bold text-white text-lg flex-1">Pulse</p>
        {[["deck", Heart], ["matches", Sparkles], ["profile", Pencil]].map(([id, Icon]) => (
          <button key={id} data-testid={`pulse-tab-${id}`} onClick={() => { setTab(id); sfx?.keyTick(); }}
            className={`p-2 rounded-xl border transition-colors ${tab === id ? "border-pink-500/50 text-pink-400 bg-pink-500/10" : "border-white/10 text-white/40"}`}>
            <Icon className="w-3.5 h-3.5" />
          </button>
        ))}
      </div>

      {tab === "deck" && (
        !card ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 py-10">
            <Heart className="w-8 h-8 text-white/15" />
            <p className="font-mono text-[9px] text-white/40 text-center leading-relaxed">Deck's empty.<br />Bornwood is playing hard to get — check back later.</p>
          </div>
        ) : (
          <div data-testid="pulse-card" className="border border-pink-500/25 bg-gradient-to-b from-pink-500/[0.08] to-transparent rounded-3xl p-5 flex flex-col gap-3" style={{ animation: "phAppIn 0.25s ease both" }}>
            <div className="w-16 h-16 rounded-full border-2 border-pink-500/40 bg-pink-500/10 flex items-center justify-center mx-auto">
              <span className="font-sans font-bold text-xl text-pink-300">{card.name.slice(0, 1)}</span>
            </div>
            <p className="font-sans font-bold text-white text-center text-lg">{card.name}</p>
            {card.tagline && <p className="font-mono text-[10px] text-white/60 text-center italic">"{card.tagline}"</p>}
            <div className="flex flex-wrap justify-center gap-1.5">
              {(card.interests || []).map((i) => <span key={i} className="font-mono text-[7px] uppercase tracking-[0.15em] border border-white/15 text-white/50 px-2 py-1 rounded-full">{i}</span>)}
              <span className="font-mono text-[7px] uppercase tracking-[0.15em] border border-pink-500/30 text-pink-300 px-2 py-1 rounded-full">looking: {card.looking}</span>
            </div>
            <div className="flex gap-3 mt-2">
              <button data-testid="pulse-pass" onClick={() => swipe(card.pc, "pass")} disabled={!!busy}
                className="flex-1 py-3.5 rounded-2xl border border-white/15 text-white/60 font-mono text-[10px] uppercase tracking-[0.25em] font-bold hover:bg-white/5 transition-colors flex items-center justify-center gap-2">
                <X className="w-4 h-4" /> Pass
              </button>
              <button data-testid="pulse-like" onClick={() => swipe(card.pc, "like")} disabled={!!busy}
                className="flex-1 py-3.5 rounded-2xl bg-pink-500 text-white font-mono text-[10px] uppercase tracking-[0.25em] font-bold hover:bg-pink-400 transition-colors flex items-center justify-center gap-2">
                {busy === card.pc ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Heart className="w-4 h-4 fill-white" /> Like</>}
              </button>
            </div>
            <p className="font-mono text-[7px] text-white/25 text-center uppercase tracking-[0.2em]">{deck.length} in the deck</p>
          </div>
        )
      )}

      {tab === "matches" && (
        matches.length === 0 ? (
          <p className="font-mono text-[9px] text-white/30 text-center py-8">No matches yet — get swiping.</p>
        ) : matches.map((m) => (
          <div key={m.id} data-testid={`pulse-match-${m.id}`} className="border border-pink-500/25 bg-pink-500/[0.05] rounded-2xl px-4 py-3.5">
            <p className="font-mono text-[10px] font-bold text-white flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-pink-400" /> {m.with_name}</p>
            <p className="font-mono text-[8px] text-white/50 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> suggested first meet: {m.spot}</p>
          </div>
        ))
      )}

      {tab === "profile" && (
        <div className="border border-white/10 bg-white/[0.03] rounded-2xl p-4 flex flex-col gap-3">
          <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/40">Your Pulse profile</p>
          <input data-testid="pulse-tagline" value={form.tagline} maxLength={90} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
            placeholder="Tagline — one line that sells you"
            className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 font-mono text-[10px] text-white placeholder:text-white/30 focus:outline-none" />
          <input data-testid="pulse-interests" value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })}
            placeholder="Interests, comma separated (max 5)"
            className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 font-mono text-[10px] text-white placeholder:text-white/30 focus:outline-none" />
          <div className="flex gap-1.5">
            {["romance", "friends", "crew", "everyone"].map((l) => (
              <button key={l} data-testid={`pulse-looking-${l}`} onClick={() => setForm({ ...form, looking: l })}
                className={`flex-1 py-2 rounded-xl font-mono text-[7px] uppercase tracking-[0.1em] border transition-colors ${form.looking === l ? "border-pink-500/50 text-pink-300 bg-pink-500/10" : "border-white/10 text-white/40"}`}>{l}</button>
            ))}
          </div>
          <label data-testid="pulse-active" className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-pink-500" />
            <span className="font-mono text-[9px] text-white/60">Show me in the deck</span>
          </label>
          <button data-testid="pulse-save" onClick={saveProfile} disabled={busy === "save"}
            className="w-full py-3 rounded-xl bg-pink-500 text-white font-mono text-[10px] uppercase tracking-[0.25em] font-bold hover:bg-pink-400 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Save profile</>}
          </button>
        </div>
      )}
    </div>
  );
}
